<template>
    <section class="dashboard-page">
        <div v-if="isLoading" class="dashboard-loading">
            <div class="loading"><div></div></div>
            <p class="fonte-fraca">Carregando painel...</p>
        </div>

        <div v-else-if="!opData.length" class="dashboard-empty">
            <p class="dashboard-empty-title">Nenhuma máquina em produção</p>
            <p class="fonte-fraca">As informações aparecerão aqui quando houver OPs em andamento nas injetoras.</p>
        </div>

        <div v-else class="dashboard-grid">
            <article
                v-for="(ele, idx) in opData"
                :key="ele.machineName + (ele.opNum || '') + idx"
                class="dashboard-card"
                :class="{ 'dashboard-card--paused': ele.isPaused }"
            >
                <header class="dashboard-card__header">
                    <h2 class="dashboard-card__machine">{{ ele.machineName }}</h2>
                    <h1 class="dashboard-card__product" :title="ele.productName">{{ ele.productName }}</h1>
                </header>

                <div class="dashboard-card__meta grid-3">
                    <div class="bloco margem">
                        <div class="fonte-fraca">OP</div>
                        <div class="lead">{{ formatOpNum(ele.opNum) }}</div>
                    </div>
                    <div class="bloco margem">
                        <div class="fonte-fraca">Quantidade</div>
                        <div class="lead">{{ ele.qtExpected }}</div>
                    </div>
                    <div class="bloco margem" :class="isDeliveryLate(ele.deliveryDate)">
                        <div class="fonte-fraca">Entrega</div>
                        <div class="lead">{{ formatDate(ele.deliveryDate) }}</div>
                    </div>
                </div>

                <div class="grid-2 margem">
                    <div class="bloco margem">
                        <div class="fonte-fraca">Status</div>
                        <span class="dashboard-card__status" :class="ele.isPaused ? 'dashboard-card__status--paused' : 'dashboard-card__status--running'">
                            {{ ele.isPaused ? "Parada" : "Operando" }}
                        </span>
                    </div>
                    <div class="bloco margem" :class="ele.isPaused ? 'bg-atrasado' : 'bg-branco'">
                        <div class="fonte-fraca">Tempo parado</div>
                        <div class="lead">{{ formatDowntimePercent(ele.downTimeSeconds, ele.totalTimeMinutes) }}</div>
                    </div>
                </div>

                <div class="margem bloco container">
                    <div class="fonte-fraca">Tempo médio por peça</div>
                    <div class="lead">{{ formatAverageTime(ele) }}</div>
                </div>

                <div class="bloco margem bg-sucesso dashboard-card__progress-wrap">
                    <div class="grid-2" style="padding-bottom: 15px">
                        <div class="coluna bloco margem">
                            <div class="fonte-fraca">Peças produzidas</div>
                            <div class="lead" style="color: black;">{{ ele.qtyDone }}</div>
                        </div>
                        <div class="coluna bloco margem">
                            <div class="fonte-fraca">Última atualização</div>
                            <div class="lead" style="color: black;">{{ formatLastUpdate(ele.lastUpdate) }}</div>
                        </div>
                    </div>
                    <div class="dashboard-card__progress-row">
                        <progress :value="progressPercent(ele)" max="100" :title="`${progressPercent(ele)}%`"></progress>
                        <span class="dashboard-card__progress-label">{{ progressPercent(ele) }}%</span>
                    </div>
                </div>
            </article>
        </div>
    </section>
</template>

<script>
import { InjectionPanelDatasource } from "@/data/datasource/InjectionPanelDatasouce";

const dataSource = new InjectionPanelDatasource();

export default {
    data() {
        return {
            opData: [],
            interval: null,
            isLoading: true,
        };
    },
    async created() {
        await this.loadData();
    },
    mounted() {
        this.interval = setInterval(() => this.loadData(), 15000);
    },
    beforeUnmount() {
        if (this.interval) clearInterval(this.interval);
    },
    methods: {
        async loadData() {
            try {
                if (this.opData.length === 0) this.isLoading = true;
                this.opData = await dataSource.getData();
            } catch (e) {
                console.error("InjetoraDashboard loadData:", e);
                this.opData = [];
            } finally {
                this.isLoading = false;
            }
        },

        formatOpNum(opNum) {
            if (opNum == null || opNum === "") return "—";
            const s = String(opNum);
            const slash = s.indexOf("/");
            return slash >= 0 ? s.slice(slash + 1) : s;
        },

        formatDate(date) {
            if (date == null) return "—";
            return new Date(date).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                // year: "numeric",
            });
        },

        formatLastUpdate(isoDate) {
            if (isoDate == null) return "—";
            const d = new Date(isoDate);
            if (isNaN(d.getTime())) return "—";
            return d.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        formatDowntimePercent(partial, total) {
            if (total == null || total === 0) return "0%";
            const pct = (Number(partial) / Number(total)) * 100;
            return `${Number(pct.toFixed(1))}%`;
        },

        formatAverageTime(op) {
            if (op.qtyDone == null || op.qtyDone <= 0) return "—";
            const sec = Math.floor(op.machineSeconds / op.qtyDone);
            return `${sec}s`;
        },

        progressPercent(op) {
            if (op.qtExpected == null || op.qtExpected <= 0) return 0;
            const done = Number(op.qtyDone) || 0;
            return Math.min(100, Math.round((done / op.qtExpected) * 100));
        },

        isDeliveryLate(deliveryDate) {
            if (deliveryDate == null) return "";
            const delivery = new Date(deliveryDate).getTime();
            const today = new Date().setHours(0, 0, 0, 0);
            return delivery < today ? "bg-blink" : "bg-branco";
        },
    },
};
</script>

<style scoped>
/* TV-optimized: 3 machines, large typography and spacing for viewing from a distance */

.dashboard-page {
    padding: 32px 24px;
    min-height: 100vh;
    box-sizing: border-box;
}

.dashboard-loading,
.dashboard-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    padding: 80px 48px;
    text-align: center;
    min-height: 60vh;
}

.dashboard-loading .loading {
    width: 80px;
    height: 80px;
}

.dashboard-empty-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--cor-fonte-forte);
}

.dashboard-empty .fonte-fraca {
    font-size: 1.5rem;
}

/* Always 3 columns: one card per injection machine */
.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    align-items: stretch;
    width: 100%;
    max-width: 100%;
}

.dashboard-card {
    min-width: 0; /* allow shrink in grid */
    border-radius: 16px;
    border: 3px solid var(--cor-separador);
    padding: 0 28px 28px;
    background: var(--cor-bg);
    box-shadow: 0 4px 32px var(--cor-cinza);
    display: flex;
    flex-direction: column;
}

.dashboard-card--paused {
    border-color: var(--cor-alerta);
    background: linear-gradient(0deg, var(--cor-alerta) 0%, var(--cor-bg) 14%);
}

.dashboard-card__header {
    padding: 24px 0;
    border-bottom: 3px solid var(--cor-separador);
    text-align: center;
}

.dashboard-card__machine {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--cor-fonte-fraca);
    margin: 0 0 8px;
    padding: 0;
}

.dashboard-card__product {
    font-size: 2.25rem;
    line-height: 1.25;
    margin: 0;
    padding: 0;
    color: var(--cor-fonte-forte);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 700;
}

.dashboard-card__meta .lead,
.dashboard-card__progress-wrap .lead {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dashboard-card__status {
    display: inline-block;
    padding: 10px 20px;
    border-radius: 28px;
    font-size: 1.5rem;
    font-weight: 700;
}

.dashboard-card__status--running {
    background: var(--cor-primaria-fraca);
    color: var(--cor-primaria-forte);
}

.dashboard-card__status--paused {
    background: var(--cor-alerta);
    color: #000;
}

.dashboard-card__progress-row {
    display: flex;
    align-items: center;
    gap: 20px;
}

.dashboard-card__progress-row progress {
    flex: 1;
    margin: 0;
    height: 28px;
    border-radius: 14px;
}

.dashboard-card__progress-row progress::-webkit-progress-bar {
    border-radius: 14px;
}

.dashboard-card__progress-row progress::-webkit-progress-value {
    border-radius: 14px;
}

.dashboard-card__progress-label {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--cor-fonte-forte);
    min-width: 3em;
    text-align: right;
}

.lead {
    font-size: 2.5rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.margem {
    margin: 0;
    padding: 20px;
}

.bg-branco {
    color: var(--cor-fonte);
}

.bg-atrasado {
    background-color: var(--cor-alerta);
    color: #000;
}

.bg-blink {
    animation: blink 1s infinite alternate;
}

@keyframes blink {
    from {
        color: var(--cor-fonte);
        background-color: var(--cor-bg);
    }
    to {
        color: var(--cor-bg);
        background-color: var(--cor-erro);
    }
}

.fonte-fraca {
    font-size: 1.25rem;
    color: var(--cor-fonte-fraca);
}
</style>
